"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _componentsBase = require("@telus-uds/components-base");
var _portal = require("@gorhom/portal");
var _styledComponents = _interopRequireDefault(require("styled-components"));
var _SpinnerContent = _interopRequireDefault(require("./SpinnerContent"));
var _utils = require("../utils");
var _constants = require("./constants");
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const [selectProps, selectedSystemPropTypes] = (0, _componentsBase.selectSystemProps)([_utils.htmlAttrs]);
const SpinnerContainer = /*#__PURE__*/_styledComponents.default.div.withConfig({
  displayName: "Spinner__SpinnerContainer",
  componentId: "components-web__sc-116rqck-0"
})(_ref => {
  let {
    inline,
    fullScreen,
    overlay
  } = _ref;
  return {
    position: 'relative',
    ...(inline && {
      display: overlay ? 'inline-block' : 'block',
      ...(0, _utils.media)().from('md').css({
        display: 'inline-block'
      })
    }),
    ...(fullScreen && {
      top: '50%',
      transform: 'translateY(-50%)'
    })
  };
});
const ContentOverlay = /*#__PURE__*/_styledComponents.default.div.withConfig({
  displayName: "Spinner__ContentOverlay",
  componentId: "components-web__sc-116rqck-1"
})({
  position: 'absolute',
  width: '100%',
  height: '100%',
  zIndex: _constants.BACKDROP_Z_INDEX
});
const FullscreenOverlay = /*#__PURE__*/_styledComponents.default.div.withConfig({
  displayName: "Spinner__FullscreenOverlay",
  componentId: "components-web__sc-116rqck-2"
})(_ref2 => {
  let {
    fullScreenOverLayBackground
  } = _ref2;
  return {
    position: 'fixed',
    width: '100vw',
    height: '100vh',
    top: 0,
    left: 0,
    zIndex: _constants.BACKDROP_Z_INDEX,
    backgroundColor: fullScreenOverLayBackground
  };
});
const OpaqueContainer = /*#__PURE__*/_styledComponents.default.div.withConfig({
  displayName: "Spinner__OpaqueContainer",
  componentId: "components-web__sc-116rqck-3"
})({
  opacity: _constants.BACKDROP_OPACITY
});
const recursiveMap = (children, fn) => _react.default.Children.map(children, child => {
  if (! /*#__PURE__*/_react.default.isValidElement(child)) {
    return child;
  }
  if (child.props.children) {
    return fn(/*#__PURE__*/_react.default.cloneElement(child, {
      children: recursiveMap(child.props.children, fn)
    }));
  }
  return fn(child);
});

/**
 * Loading indicator.
 */
const Spinner = /*#__PURE__*/_react.default.forwardRef((_ref3, ref) => {
  let {
    children,
    fullScreen = false,
    inline = false,
    label,
    labelPosition,
    show = false,
    isStatic = false,
    tokens,
    variant = {},
    ...rest
  } = _ref3;
  const {
    fullScreenOverLayBackground,
    size,
    thickness
  } = (0, _componentsBase.useThemeTokens)('Spinner', tokens, variant);
  const {
    size: sizeVariant = 'large'
  } = variant;
  (0, _componentsBase.useScrollBlocking)([fullScreen, show]);
  if (!show) {
    return children ?? null;
  }

  // Full screen spinner
  if (fullScreen) {
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(_portal.Portal, {
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)(FullscreenOverlay, {
        fullScreenOverLayBackground: fullScreenOverLayBackground,
        children: /*#__PURE__*/(0, _jsxRuntime.jsx)(SpinnerContainer, {
          inline: inline,
          fullScreen: fullScreen,
          "aria-live": "assertive",
          ...selectProps(rest),
          children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_SpinnerContent.default, {
            label: label,
            labelPosition: labelPosition,
            overlay: true,
            size: size,
            thickness: thickness,
            sizeVariant: sizeVariant,
            isStatic: isStatic
          })
        })
      })
    });
  }

  // Overlay spinner
  if (children) {
    return /*#__PURE__*/(0, _jsxRuntime.jsxs)(SpinnerContainer, {
      inline: inline,
      "aria-live": "assertive",
      overlay: true,
      ...selectProps(rest),
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_SpinnerContent.default, {
        label: label,
        labelPosition: labelPosition,
        overlay: true,
        size: size,
        thickness: thickness,
        sizeVariant: sizeVariant,
        isStatic: isStatic
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)(ContentOverlay, {}), /*#__PURE__*/(0, _jsxRuntime.jsx)(OpaqueContainer, {
        inert: "true",
        children: recursiveMap(children, c => {
          if (c) {
            return /*#__PURE__*/_react.default.cloneElement(c, {
              tabIndex: '-1',
              'aria-hidden': 'true'
            });
          }
          return undefined;
        })
      })]
    });
  }

  // Standalone spinner
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(SpinnerContainer, {
    ref: ref,
    ...selectProps(rest),
    children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_SpinnerContent.default, {
      label: label,
      labelPosition: labelPosition,
      size: size,
      thickness: thickness,
      sizeVariant: sizeVariant,
      isStatic: isStatic
    })
  });
});
Spinner.displayName = 'Spinner';
Spinner.propTypes = {
  ...selectedSystemPropTypes,
  tokens: (0, _componentsBase.getTokensPropType)('Spinner'),
  /**
   * Content to be overlaid while the spinner is active. Can be text, any HTML element,
   * or any component.
   */
  children: _propTypes.default.node,
  /**
   * Enables body locking.
   */
  fullScreen: _propTypes.default.bool,
  /**
   * Set the inline prop to true if the Spinner should only cover its children; if
   * the Spinner should cover the full width of its parent regardless of the size of
   * its children, inline should be set to false
   */
  inline: _propTypes.default.bool,
  /**
   * Communicates a message to assistive technology while visible. This same message
   * will appear underneath the spinner when its `size` is `large`.
   */
  label: _propTypes.default.string.isRequired,
  /**
   * The size of the spinner
   */
  // size: PropTypes.oneOf(['large', 'small']),
  /**
   * Whether or not to render the spinner.
   */
  show: _propTypes.default.bool,
  /**
   * If true, it should render a static spinner
   */
  isStatic: _propTypes.default.bool,
  /**
   * Determine where the label of the spinner should be placed, left, right, bottom or top.
   */
  labelPosition: _propTypes.default.string
};
var _default = exports.default = Spinner;